import React, { useState, useEffect } from 'react';
import ShadeTypeSelector from './components/ShadeTypeSelector';
import SizeConfigurator from './components/SizeConfigurator';
import ColorSelector from './components/ColorSelector';
import OrderConfirmation from './components/OrderConfirmation';
import { trackShadeBuilderStart, startStepTimer } from './services/analytics';

// Feature flag: Set to true to show Rush Order promo and checkbox, false to hide
const SHOW_RUSH_ORDER = false;

// Parse URL parameters to state
function parseUrlToState() {
  const params = new URLSearchParams(window.location.search);
  
  return {
    selectedType: params.get('type') || null,
    shadeConfiguration: params.get('config') || null,
    dimensions: {
      width: params.get('width') ? parseFloat(params.get('width')) : 48,
      height: params.get('height') ? parseFloat(params.get('height')) : 36
    },
    colors: {
      topRail: params.get('topRail') || null,
      nightShade: params.get('nightShade') || null,
      dayShade: params.get('dayShade') || null
    },
    rushOrder: params.get('rush') === 'true',
    nonRefundableAcknowledged: false, // Always false on load for safety
    step3Complete: false // Managed by ColorSelector based on available options
  };
}

// Convert state to URL parameters
function stateToUrlParams(state) {
  const params = new URLSearchParams();
  
  if (state.selectedType) params.set('type', state.selectedType);
  if (state.shadeConfiguration) params.set('config', state.shadeConfiguration);
  if (state.dimensions.width !== 48) params.set('width', state.dimensions.width);
  if (state.dimensions.height !== 36) params.set('height', state.dimensions.height);
  if (state.colors.topRail) params.set('topRail', state.colors.topRail);
  if (state.colors.nightShade) params.set('nightShade', state.colors.nightShade);
  if (state.colors.dayShade) params.set('dayShade', state.colors.dayShade);
  if (state.rushOrder) params.set('rush', 'true');
  
  return params.toString();
}

function App() {
  const [state, setState] = useState(() => parseUrlToState());

  // Track shade builder start on mount
  useEffect(() => {
    const hasUrlParams = Boolean(window.location.search);
    trackShadeBuilderStart(hasUrlParams, state);
    startStepTimer(1);
  }, []);

  // Update URL when state changes
  useEffect(() => {
    const urlParams = stateToUrlParams(state);
    const newUrl = urlParams ? `${window.location.pathname}?${urlParams}` : window.location.pathname;
    
    // Use replaceState to avoid creating history entries for each change
    window.history.replaceState({}, '', newUrl);
  }, [state]);

  const updateState = (updates) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const isStep1Complete = state.selectedType && state.shadeConfiguration;
  const isStep2Complete = isStep1Complete && state.dimensions.width && state.dimensions.height;
  const isStep3Complete = isStep2Complete && state.step3Complete;

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-py-8">
      <div className="tw-max-w-4xl tw-mx-auto tw-px-4 tw-sm:px-6 tw-lg:px-8">
        {/* Header */}
        <div className="tw-text-left tw-mb-8">
          <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-3 tw-mb-4">
            <p className="tw-text-sm tw-text-blue-800">
              Don't want to measure? <a href="/products/irvine-shade-custom-replacement-service-send-us-your-broken-shade" className="tw-font-semibold tw-underline hover:tw-text-blue-600">Send us your old shade</a> and we'll match it for you!
            </p>
          </div>
          <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">
            RV Window Shade Builder
          </h1>
          <p className="tw-text-gray-600">
            Custom build your perfect RV/Motorhome window shades! Custom made in Elkhart, Indiana USA by Irvine Shade. We offer pleated cellular shades and roller shades in day/night and night configurations. All shades are made to order, just for you!
          </p>
          <div className="tw-py-4 tw-text-brand-red tw-text-lg">
            <ul>
              <li className="tw-font-semibold">LIMITED PROMO:</li>
              <li><strong>Free Mounting Brackets</strong> with every shade you buy</li>
              {SHOW_RUSH_ORDER && (
                <li><strong>Free Rush Order</strong> ($49 value) with 3+ Shades!</li>
              )}
            </ul>
          </div>
        </div>

        {/* Step 1: Shade Type Selection */}
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-mb-6">
          <div className="tw-flex tw-items-center tw-mb-4">
            <div className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-brand-red tw-text-white tw-font-medium tw-text-sm tw-mr-3">
              1
            </div>
            <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">
              Shade Type
            </h2>
            {isStep1Complete && (
              <div className="tw-ml-auto tw-text-green-600">
                <svg className="tw-w-5 tw-h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
          <ShadeTypeSelector
            selectedType={state.selectedType}
            shadeConfiguration={state.shadeConfiguration}
            onUpdate={updateState}
          />
        </div>

        {/* Step 2: Size Configuration */}
        {isStep1Complete && (
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-mb-6 tw-animate-fade-in">
            <div className="tw-flex tw-items-center tw-mb-4">
              <div className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-brand-red tw-text-white tw-font-medium tw-text-sm tw-mr-3">
                2
              </div>
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">
                Size
              </h2>
              {isStep2Complete && (
                <div className="tw-ml-auto tw-text-green-600">
                  <svg className="tw-w-5 tw-h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            <SizeConfigurator
              dimensions={state.dimensions}
              selectedType={state.selectedType}
              shadeConfiguration={state.shadeConfiguration}
              onUpdate={updateState}
            />
          </div>
        )}

        {/* Step 3: Color Selection */}
        {isStep2Complete && (
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-mb-6 tw-animate-fade-in">
            <div className="tw-flex tw-items-center tw-mb-4">
              <div className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-brand-red tw-text-white tw-font-medium tw-text-sm tw-mr-3">
                3
              </div>
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">
                Colors
              </h2>
              {isStep3Complete && (
                <div className="tw-ml-auto tw-text-green-600">
                  <svg className="tw-w-5 tw-h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            <ColorSelector
              selectedType={state.selectedType}
              colors={state.colors}
              shadeConfiguration={state.shadeConfiguration}
              onUpdate={updateState}
            />
          </div>
        )}

        {/* Step 4: Order Confirmation */}
        {isStep3Complete && (
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-mb-6 tw-animate-fade-in">
            <div className="tw-flex tw-items-center tw-mb-4">
              <div className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-brand-red tw-text-white tw-font-medium tw-text-sm tw-mr-3">
                4
              </div>
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">
                Add to Cart
              </h2>
            </div>
            <OrderConfirmation
              state={state}
              onUpdate={updateState}
              showRushOrder={SHOW_RUSH_ORDER}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default App;